import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, AlertTriangle, Truck, Wrench as WrenchIcon, MoreHorizontal, Clock, CheckCircle2, User, MapPin, ExternalLink } from "lucide-react";
import { fileUrl } from "@/lib/placeholders";
import NewReportDialog from "@/components/NewReportDialog";

const TYPE_ICON = { "Near Miss": AlertTriangle, "Non Conformita": Truck, "Segnalazione": WrenchIcon, "Comunicazione": MoreHorizontal };
const SEVERITY_COLOR = { bassa: "bg-emerald-100 text-emerald-700", media: "bg-amber-100 text-amber-700", alta: "bg-red-100 text-red-700" };
const STATUS_LABEL = { aperta: "Aperta", presa_in_carico: "In lavorazione", chiusa: "Chiusa" };
const STATUS_COLOR = { aperta: "bg-red-100 text-red-700", presa_in_carico: "bg-amber-100 text-amber-700", chiusa: "bg-emerald-100 text-emerald-700" };
const FILTERS = [["aperta", "Aperte"], ["presa_in_carico", "In lavorazione"], ["chiusa", "Chiuse"], ["all", "Tutte"]];

export default function Segnalazioni() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("aperta");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  // Dal QR: la segnalazione parte gia' collegata al mezzo o al cantiere.
  const [dalQr, setDalQr] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("mezzo")) return { equipment: p.get("mezzo") };
      if (p.get("cantiere")) return { site: p.get("cantiere"), nome: p.get("nome") || "" };
    } catch { /* niente */ }
    return null;
  });

  const load = async (f) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/reports?status=${f}`);
      setList(data);
    } catch { toast.error("Errore caricamento segnalazioni"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(filter); }, [filter]);

  // "Non letta" vuol dire: indirizzata a me e mai aperta da me. Non è lo
  // stato della segnalazione — quella può essere già in lavorazione da un
  // collega e restare comunque da leggere per me.
  const daLeggere = (r) => (r.destinatari || []).includes(user?.email)
    && !(r.letta_da || []).includes(user?.email);

  const apri = (r) => {
    setOpenItem(r);
    if (!daLeggere(r)) return;
    api.post(`/reports/${r.id}/letta`)
      .then(() => setList((l) => l.map((x) => (x.id === r.id
        ? { ...x, letta_da: [...(x.letta_da || []), user?.email] } : x))))
      .catch(() => {});
  };

  return (
    <div className="space-y-6" data-testid="segnalazioni-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Sicurezza</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Segnalazioni</h1>
          <p className="text-sm text-slate-500 mt-1">Near miss, non conformità, guasti e richieste dal campo</p>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12" onClick={() => setShowNew(true)} data-testid="add-report-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuova segnalazione
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => setFilter(v)}
                  className={`px-3 py-2 rounded-full text-xs font-medium border ${filter === v ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!loading && list.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          Nessuna segnalazione in questa categoria.
        </CardContent></Card>
      )}

      <div className="space-y-1.5">
        {list.map(r => {
          const Icon = TYPE_ICON[r.report_type] || MoreHorizontal;
          return (
            <Card key={r.id} data-testid={`segnalazione-${r.id}`}
                  className={`hover:shadow-sm transition cursor-pointer ${
                    daLeggere(r) ? "border-[#0067b2] border-l-4" : "border-slate-200"}`}
                  onClick={() => apri(r)}>
              <CardContent className="px-3 py-2 flex items-start gap-3">
                <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm ${daLeggere(r) ? "font-black" : "font-semibold"}`}>
                      {r.report_type}
                    </span>
                    {daLeggere(r) && (
                      <Badge className="bg-[#0067b2] text-white border-0 text-[10px]"
                             data-testid={`non-letta-${r.id}`}>
                        Nuova per te
                      </Badge>
                    )}
                    <Badge className={`${SEVERITY_COLOR[r.severity]} border-0 text-[10px]`}>{r.severity}</Badge>
                    <Badge className={`${STATUS_COLOR[r.status]} border-0 text-[10px]`}>{STATUS_LABEL[r.status]}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 truncate mt-0.5">{r.description}</p>
                  <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-1 flex-wrap">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{r.site_name || "Magazzino/Sede"}</span>
                    {r.equipment_label && (
                      <span className="flex items-center gap-1 text-[#0067b2] font-medium">
                        <WrenchIcon className="w-3 h-3" />{r.equipment_label}
                      </span>
                    )}
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{r.created_by_name || r.created_by}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(r.created_at).toLocaleString("it-IT")}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {showNew && <NewReportDialog onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(filter); }} />}
      {dalQr && (
        <NewReportDialog defaultEquipmentId={dalQr.equipment} defaultSiteId={dalQr.site} defaultSiteName={dalQr.nome}
                         onClose={() => setDalQr(null)} onSaved={() => { setDalQr(null); load(filter); }} />
      )}
      {openItem && <ReportDetail report={openItem} onClose={() => setOpenItem(null)} onChanged={() => { setOpenItem(null); load(filter); }} />}
    </div>
  );
}

function ReportDetail({ report, onClose, onChanged }) {
  const { user } = useAuth();
  const [esito, setEsito] = useState("");
  const [busy, setBusy] = useState(false);
  // Segnalare la puo' chiunque; prenderla in carico e chiuderla no. Senza
  // questo controllo il preposto e il magazziniere vedrebbero due pulsanti
  // che rispondono "permessi insufficienti".
  const puoLavorarla = ["admin", "amministrativo", "tecnico"].includes(user?.role);

  const take = async () => {
    setBusy(true);
    try { await api.post(`/reports/${report.id}/take`); toast.success("Presa in carico"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const close = async () => {
    if (!esito.trim()) { toast.error("Indica l'esito prima di chiudere"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("esito", esito);
    try { await api.post(`/reports/${report.id}/close`, fd); toast.success("Segnalazione chiusa"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {report.report_type}
            <Badge className={`${SEVERITY_COLOR[report.severity]} border-0 text-[10px]`}>{report.severity}</Badge>
            <Badge className={`${STATUS_COLOR[report.status]} border-0 text-[10px]`}>{STATUS_LABEL[report.status]}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-1 text-slate-500">
            <MapPin className="w-3 h-3" />{report.site_name || "Magazzino/Sede"}
            {report.site_id && (
              <Link to={`/cantieri/${report.site_id}`} target="_blank" className="text-orange-600 inline-flex items-center gap-1 ml-1">
                <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
          {/* Il mezzo a cui si riferisce: da qui si va alla sua scheda, dove
              c'è il libro di manutenzione su cui l'intervento andrà scritto. */}
          {report.equipment_id && (
            <Link to={`/attrezzature/${report.equipment_id}`} target="_blank"
                  data-testid="vai-al-mezzo"
                  className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[#0067b2] font-medium">
              <WrenchIcon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate">{report.equipment_label}</span>
              <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
            </Link>
          )}
          <div className="text-slate-700">{report.description}</div>
          <div className="text-xs text-slate-400">
            Segnalato da {report.created_by_name || report.created_by} · {new Date(report.created_at).toLocaleString("it-IT")}
          </div>
          {report.destinatari?.length > 0 && (
            <div className="text-[11px] text-slate-400">
              Inviata a {report.destinatari.length} persone fra ufficio tecnico,
              amministrazione e direzione · letta da {(report.letta_da || []).length}.
            </div>
          )}

          {report.photo_urls?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {report.photo_urls.map((u, i) => (
                <a key={i} href={fileUrl(u)} target="_blank" rel="noopener noreferrer">
                  <img src={fileUrl(u)} alt="" className="w-20 h-20 object-cover rounded border border-slate-200" />
                </a>
              ))}
            </div>
          )}

          {report.status !== "chiusa" && puoLavorarla && (
            <div className="pt-2 border-t border-slate-100 space-y-2">
              {report.status === "aperta" && (
                <Button onClick={take} disabled={busy} variant="outline" className="w-full">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Prendi in carico
                </Button>
              )}
              <div>
                <textarea rows={2} className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                          placeholder="Esito / azione correttiva..." value={esito} onChange={(e) => setEsito(e.target.value)} />
              </div>
              <Button onClick={close} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                Chiudi segnalazione
              </Button>
            </div>
          )}

          {report.status !== "chiusa" && !puoLavorarla && (
            <p className="pt-2 border-t border-slate-100 text-xs text-slate-500">
              La prende in carico e la chiude l&apos;ufficio tecnico.
            </p>
          )}

          {report.status === "chiusa" && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs text-slate-400 mb-1">Esito</div>
              <div className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded p-2">{report.esito}</div>
              <div className="text-[11px] text-slate-400 mt-1">
                Chiusa da {report.closed_by} · {report.closed_at && new Date(report.closed_at).toLocaleString("it-IT")}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
